import { getSDKClient } from '../../lib/sdk-client.js';
import { checkPromptDisambiguation, type DisambiguationResult } from './guided-workflow.js';
import type { CLIDirective, AskUserQuestion } from '../../lib/directive-types.js';

// ---------------------------------------------------------------------------
// Generic dataset workflow (no prompt-id)
// ---------------------------------------------------------------------------

export interface GenericDatasetWorkflowResult {
  mode: 'generic';
  message: string;
  workflow: string[];
  formatReference: {
    item: {
      input: Record<string, string>;
      expectedOutput: Record<string, string>;
    };
  };
  suggestedCategories: {
    name: string;
    description: string;
    priority: 'high' | 'medium' | 'low';
  }[];
  _directive: CLIDirective;
}

/**
 * Returns a generic dataset workflow when no prompt-id is provided.
 * Guides the agent to upload a prompt first, then re-run with a prompt-id.
 */
export function buildGenericDatasetWorkflow(): GenericDatasetWorkflowResult {
  return {
    mode: 'generic',
    message: 'No prompt-id provided. Follow this sequence to create a dataset:',
    workflow: [
      '1. mutagent explore --json — scan codebase for prompts',
      '2. mutagent prompts create ... --json — upload the target prompt',
      '3. Re-run: mutagent prompts dataset add <prompt-id> --guided --json',
    ],
    formatReference: {
      item: {
        input: { '<inputSchema_field>': '<value>' },
        expectedOutput: { '<outputSchema_field>': '<expected_value>' },
      },
    },
    suggestedCategories: [
      { name: 'Edge Cases', description: 'Boundary inputs, empty fields, malformed data', priority: 'high' },
      { name: 'Hard Cases', description: 'Ambiguous inputs, adversarial examples', priority: 'high' },
      { name: 'Representative Cases', description: 'Typical production inputs', priority: 'medium' },
    ],
    _directive: {
      instruction: 'Ask the user which prompt they want to create a dataset for. If unclear, run mutagent explore --json first.',
      next: ['mutagent explore --json', 'mutagent prompts list --json'],
    },
  };
}

interface SuggestedCategory {
  name: string;
  description: string;
  priority: 'high' | 'medium' | 'low';
}

interface GuidedDatasetGuidance {
  minItems: number;
  priorityRule: string;
  steps: string[];
}

export interface GuidedDatasetResult {
  promptId: string;
  promptName: string;
  schemaFields: {
    input: string[];
    output: string[];
  };
  suggestedCategories: SuggestedCategory[];
  templateItem: {
    input: Record<string, string>;
    expectedOutput: Record<string, string>;
  };
  guidance: GuidedDatasetGuidance;
  _directive: CLIDirective;
  disambiguation?: DisambiguationResult;
}

/**
 * Build a guided dataset workflow for a prompt.
 * Fetches prompt schemas and constructs structured guidance that agents use
 * to collect domain-specific dataset items from users.
 */
export function buildGuidedDatasetWorkflow(prompt: {
  id: number | string;
  name: string;
  inputSchema?: unknown;
  outputSchema?: unknown;
}): GuidedDatasetResult {
  const promptId = String(prompt.id);

  const outputProperties =
    prompt.outputSchema && typeof prompt.outputSchema === 'object'
      ? (((prompt.outputSchema as Record<string, unknown>).properties ?? {}) as Record<
          string,
          { type?: string; description?: string }
        >)
      : ({} as Record<string, { type?: string; description?: string }>);

  const inputProperties =
    prompt.inputSchema && typeof prompt.inputSchema === 'object'
      ? (((prompt.inputSchema as Record<string, unknown>).properties ?? {}) as Record<
          string,
          { type?: string; description?: string }
        >)
      : ({} as Record<string, { type?: string; description?: string }>);

  const inputFields = Object.keys(inputProperties);
  const outputFields = Object.keys(outputProperties);

  // Build templateItem showing expected shape
  const templateInput: Record<string, string> = {};
  for (const f of inputFields) {
    templateInput[f] = '<value>';
  }
  const templateOutput: Record<string, string> = {};
  for (const f of outputFields) {
    templateOutput[f] = '<expected>';
  }

  // Build per-field questions (AskUserQuestion[] — moved inside _directive per D5)
  const askUserQuestions: AskUserQuestion[] = [
    // Hard/edge case opening questions
    {
      field: '_general',
      source: 'inputSchema',
      question: 'What are the hardest inputs for this prompt? Describe scenarios that typically cause failures or ambiguous outputs.',
    },
    {
      field: '_edge_cases',
      source: 'inputSchema',
      question: 'What edge cases have caused failures in production? Include boundary inputs, empty fields, and malformed data.',
    },
    // Per input field
    ...inputFields.map((f) => ({
      field: f,
      source: 'inputSchema' as const,
      question: `What values should "${f}" have in test cases? Include edge cases (empty, unicode, very long, malformed).`,
    })),
    // Per output field
    ...outputFields.map((f) => ({
      field: f,
      source: 'outputSchema' as const,
      question: `What should correct "${f}" look like? Describe the format, tone, completeness criteria, and examples of incorrect outputs.`,
    })),
  ];

  const suggestedCategories: SuggestedCategory[] = [
    {
      name: 'Edge Cases',
      description: 'Boundary inputs, empty fields, malformed data',
      priority: 'high',
    },
    {
      name: 'Hard Cases',
      description: 'Ambiguous inputs, adversarial examples, domain traps',
      priority: 'high',
    },
    {
      name: 'Representative Cases',
      description: 'Typical production inputs',
      priority: 'medium',
    },
  ];

  const totalSchemaFields = inputFields.length + outputFields.length;
  const hasSchema = totalSchemaFields > 0;

  return {
    promptId,
    promptName: prompt.name,
    schemaFields: {
      input: inputFields,
      output: outputFields,
    },
    suggestedCategories,
    templateItem: {
      input: hasSchema ? templateInput : { '<field>': '<value>' },
      expectedOutput: hasSchema ? templateOutput : { '<field>': '<expected>' },
    },
    guidance: {
      minItems: 5,
      priorityRule: 'Hard cases that expose prompt weaknesses > easy cases that always pass',
      steps: [
        'Collect answers to each question from the user via _directive.askUserQuestions',
        'Generate 5-10 dataset items covering all categories',
        'Ensure at least 2 hard/edge cases per category',
        'Format as JSON array: [{"input": {...}, "expectedOutput": {...}}, ...]',
        `Run: mutagent prompts dataset add ${promptId} -d '<json>' --name '<name>' --json`,
      ],
    },
    _directive: {
      // Bootstrappable instruction — self-sufficient EVEN WITHOUT the Skill loaded (per D6).
      // Mirrors the conceptual rules from concepts/dataset-design.md inline.
      instruction:
        'DATASET CURATION — MANDATORY RULES (self-sufficient, no Skill required):\n' +
        '\n' +
        '1. PRIORITY RULE: Hard cases that expose prompt weaknesses > easy cases that always pass.\n' +
        '   Collect HARD CASES FIRST. Ask about failure modes, edge cases, adversarial inputs.\n' +
        '\n' +
        '2. CASE CATEGORIES (collect in order):\n' +
        '   a. Edge Cases (HIGH priority): boundary inputs, empty fields, very long values, unicode, malformed data\n' +
        '   b. Hard Cases (HIGH priority): ambiguous inputs, adversarial examples, domain traps that fool the model\n' +
        '   c. Representative Cases (MEDIUM priority): typical production inputs (only after hard cases covered)\n' +
        '\n' +
        '3. PER-INPUT-FIELD: For each input field, ask what VALUES should be tested. Collect at minimum:\n' +
        '   - One boundary value (empty, null, extremely long, wrong type)\n' +
        '   - One ambiguous value (that could be interpreted multiple ways)\n' +
        '   - One representative production value\n' +
        '\n' +
        '4. PER-OUTPUT-FIELD: For each output field, ask what CORRECT looks like vs INCORRECT.\n' +
        '   You need the correctness shape to generate realistic expectedOutput values.\n' +
        '\n' +
        '5. MINIMUM: 5 dataset items total. At least 2 must be hard or edge cases.\n' +
        '\n' +
        '6. FORMAT: Each item MUST have:\n' +
        '   { "input": { <inputSchema fields> }, "expectedOutput": { <outputSchema fields> } }\n' +
        '   NEVER omit expectedOutput on labelable items — optimizer needs the ground truth.\n' +
        '\n' +
        '7. ANTI-PATTERNS (never do):\n' +
        '   - All easy cases that always pass → useless for optimization\n' +
        '   - No edge cases → optimizer never sees failure modes\n' +
        '   - Fictional inputs that won\'t happen in production → scores are misleading\n' +
        '   - Missing expectedOutput on items with knowable correct answers\n' +
        '\n' +
        `Use _directive.askUserQuestions (${String(askUserQuestions.length)} questions) to collect domain knowledge from the user.\n` +
        'For deeper conceptual background, see: .claude/skills/mutagent-cli/concepts/dataset-design.md',
      next: [
        `mutagent prompts dataset add ${promptId} -d '<constructed-json>' --name '<name>' --json`,
      ],
      askUserQuestions,
    },
  };
}

/**
 * Fetch prompt and build guided dataset workflow.
 * Async wrapper around buildGuidedDatasetWorkflow for CLI use.
 */
export async function fetchAndBuildGuidedDatasetWorkflow(
  promptId: string,
): Promise<GuidedDatasetResult> {
  const client = await getSDKClient();
  const prompt = await client.getPrompt(promptId);
  const result = buildGuidedDatasetWorkflow(prompt as Parameters<typeof buildGuidedDatasetWorkflow>[0]);

  // Attach disambiguation info if similar prompts exist (non-critical)
  const disambiguation = await checkPromptDisambiguation(promptId, prompt.name);
  if (disambiguation) {
    result.disambiguation = disambiguation;
  }

  return result;
}
